import { customHeaders, requestApi } from 'api/api'
import { v4 as uuidv4 } from 'uuid'
import { BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import { WithPaginationPaging } from 'types/pagination'
import {
  TransferRequest,
  TransferResponse,
  TransferWalletsReponse,
  TransferWalletsRequest,
} from 'types/unifiedPayouts/unifiedPayouts'
import {
  PayUnifiedPayoutsV2Payload,
  UnifiedPayoutsV2AuthRequest,
  UnifiedPayoutsV2AuthResponse,
  UnifiedPayoutsV2Response,
} from 'types/unifiedPayouts/unifiedPayoutsV2'

export const postPayoutsTransfer = (data: TransferRequest): Promise<TransferResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/payment/v3/transfers',
    method: 'POST',
    data,
    headers: {
      ...customHeaders,
      'idempotency-key': uuidv4(),
    } as any,
  })
}

const buildTransferWalletsQuery = (params: Omit<TransferWalletsRequest, 'walletId'>): string => {
  const queryFields: Array<{ filterKey: string; value?: string }> = [
    { filterKey: 'currency', value: params?.currencyCode },
    { filterKey: 'walletType', value: params?.walletType },
    { filterKey: 'customerId', value: params?.customerReference },
    { filterKey: 'name', value: params?.walletName ? params.walletName : undefined },
  ]

  return queryFields
    .filter((field): field is { filterKey: string; value: string } => !!field.value)
    .map(({ filterKey, value }) => {
      const escapedValue = value.replaceAll('\\', String.raw`\\`).replaceAll('"', String.raw`\"`)
      return `${filterKey}:"${escapedValue}"`
    })
    .join(' AND ')
}

export const getPayoutsTransferWallets = ({
  walletId,
  ...params
}: TransferWalletsRequest): Promise<WithPaginationPaging<TransferWalletsReponse[]>> => {
  const searchQuery = buildTransferWalletsQuery(params) || undefined
  const { pageNumber, pageSize } = params || {}

  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/payment/v3/transfers/${walletId}/beneficiaries`,
    method: 'GET',
    params: {
      ...(searchQuery && { q: searchQuery }),
      ...(pageNumber !== undefined && pageNumber !== null && { pageNumber }),
      ...(pageSize !== undefined && pageSize !== null && { pageSize }),
    },
  })
}

export const postUnifiedPayoutsV2PreAuth = (
  payload: PayUnifiedPayoutsV2Payload
): Promise<UnifiedPayoutsV2Response> => {
  const { verificationId, request } = payload

  const sendVerificationIdHeader =
    !!verificationId &&
    request?.currency === BeneficiaryDetailsCurrency.EUR &&
    request?.method !== PaymentMethods.SWIFT

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/payment/v2/payouts',
    method: 'POST',
    data: request,
    headers: {
      ...customHeaders,
      'x-idempotency-key': uuidv4(),
      ...(sendVerificationIdHeader && { 'x-verification-id': verificationId }),
    } as any,
  })
}

export const postUnifiedPayoutsV2PostAuth = (
  data: UnifiedPayoutsV2AuthRequest
): Promise<UnifiedPayoutsV2AuthResponse> => {
  return requestApi({
    url: '',
    baseUrl: data?.mfaCallbackUrl,
    method: 'POST',
    data: {
      challenge: data?.challenge,
      tokenResponse: data?.tokenResponse,
    },
    headers: {
      'x-idempotency-key': uuidv4(),
    } as any,
  })
}
